/**
 * Toast Notification Utility
 * Wrapper around react-hot-toast for consistent notifications
 */

import toast from 'react-hot-toast';

/**
 * Custom toast styles matching Odisha Zaika brand
 */
const toastOptions = {
  // Default options for all toasts
  duration: 4000,
  position: 'top-center',

  // Styling
  style: {
    borderRadius: '8px',
    background: '#333',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    padding: '12px 16px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },

  // Success styles
  success: {
    duration: 3000,
    icon: '✅',
    style: {
      background: '#10b981',
      color: '#fff',
    },
  },

  // Error styles
  error: {
    duration: 5000,
    icon: '❌',
    style: {
      background: '#ef4444',
      color: '#fff',
    },
  },

  // Loading styles
  loading: {
    icon: '⏳',
    style: {
      background: '#3b82f6',
      color: '#fff',
    },
  },
};

/**
 * Show success toast
 */
export const showSuccess = (message) => {
  return toast.success(message, toastOptions.success);
};

/**
 * Show error toast
 */
export const showError = (message) => {
  return toast.error(message, toastOptions.error);
};

/**
 * Show loading toast
 */
export const showLoading = (message) => {
  return toast.loading(message, toastOptions.loading);
};

/**
 * Show info toast
 */
export const showInfo = (message) => {
  return toast(message, {
    ...toastOptions,
    icon: 'ℹ️',
    style: {
      ...toastOptions.style,
      background: '#3b82f6',
    },
  });
};

/**
 * Show warning toast
 */
export const showWarning = (message) => {
  return toast(message, {
    ...toastOptions,
    icon: '⚠️',
    style: {
      ...toastOptions.style,
      background: '#f59e0b',
    },
  });
};

/**
 * Show custom toast
 */
export const showCustom = (message, icon, options = {}) => {
  return toast(message, {
    ...toastOptions,
    ...options,
    icon,
  });
};

/**
 * Dismiss toast by ID
 */
export const dismissToast = (toastId) => {
  toast.dismiss(toastId);
};

/**
 * Dismiss all toasts
 */
export const dismissAllToasts = () => {
  toast.dismiss();
};

/**
 * Promise toast - shows loading, then success or error
 * Useful for async operations
 */
export const showPromise = (promise, messages) => {
  return toast.promise(
    promise,
    {
      loading: messages.loading || 'Loading...',
      success: messages.success || 'Success!',
      error: messages.error || 'Something went wrong',
    },
    toastOptions
  );
};

// Export default toast for advanced usage
const toastAPI = {
  success: showSuccess,
  error: showError,
  loading: showLoading,
  info: showInfo,
  warning: showWarning,
  custom: showCustom,
  dismiss: dismissToast,
  dismissAll: dismissAllToasts,
  promise: showPromise,
};

export default toastAPI;
